import { useSyncExternalStore } from "react";

/*
 * Is the CRM reachable? Two signals: the browser's own online/offline events, and src/api/client.ts reporting that the
 * server stopped (or started) answering ("crm:unreachable" / "crm:reachable"), which also covers a phone that has a
 * signal but no route to the server. While unreachable, /api/v1/health is asked every 15 seconds (it holds no data).
 */

export type NetState = { online: boolean };

let state: NetState = { online: typeof navigator === "undefined" ? true : navigator.onLine };
const listeners = new Set<() => void>();
let probe = 0;

function set(online: boolean) {
  if (online !== state.online) {
    state = { online };
    listeners.forEach((l) => l());
  }
  window.clearInterval(probe);
  if (!online) probe = window.setInterval(() => void check(), 15_000);
}

/** Asks the server once; true when it answered. */
export async function check() {
  try {
    const res = await fetch("/api/v1/health", { cache: "no-store" });
    set(res.status < 500);
  } catch {
    set(false);
  }
  return state.online;
}

let started = false;
/** From main.tsx before the first request, so no early "unreachable" is missed. */
export function startNetwork() {
  if (started) return;
  started = true;
  window.addEventListener("offline", () => set(false));
  window.addEventListener("online", () => void check());
  window.addEventListener("crm:unreachable", () => set(false));
  window.addEventListener("crm:reachable", () => set(true));
  if (!navigator.onLine) set(false);
}

export const isOnline = () => state.online;

export function onNetwork(l: () => void) {
  startNetwork();
  listeners.add(l);
  return () => void listeners.delete(l);
}

export function useNetwork() {
  return useSyncExternalStore(onNetwork, () => state);
}
